import type { InsightsComparisonWindow } from '@/components/ReviewsManagement/InsightsHub/insightsWindow'
import { makeNumber } from '@/helpers/chartFormat'
import { inWindow, percentTrend, previousWindowRatio, roundEstimate, sumInWindow, type PercentTrend } from '@/helpers/insightsMetrics'

import { metricState, windowState, type InsightsMetricState } from './insightsState'
import { visibilityCaption, type CompetitionVisibilityCaption } from './mapCompetitionInsights'
import type { BuyerInsightsQuery } from '../../reviews.generated'

export interface BuyerWindowTotal {
  value: string
  trend?: PercentTrend
  caption: CompetitionVisibilityCaption
}

export interface BuyerInsightsView {
  profileViews: BuyerWindowTotal
  clickOuts: BuyerWindowTotal
  visitingCompanies: string
  states: {
    clickout: InsightsMetricState
    visiting: InsightsMetricState
  }
}

type BuyerResults = NonNullable<NonNullable<BuyerInsightsQuery['manage']>['insights']>['results']
type BuyerResponse = NonNullable<BuyerResults[number]['response']>
type ClickoutResponse = Extract<BuyerResponse, { __typename?: 'ManageInsightsClickoutRateResponse' }>
type VisitingCompaniesResponse = Extract<BuyerResponse, { __typename?: 'ManageInsightsVisitingCompaniesResponse' }>
type ClickoutRow = ClickoutResponse['rows'][number]

const CLICKOUT_METRIC = 'clickout-rate'
const VISITING_METRIC = 'visiting-companies'

const countsState = (state: InsightsMetricState): InsightsMetricState => (state === 'below-guardrail' ? 'ready' : state)

const formatCount = (value: number | null | undefined, locale: string): string => (value == null ? '—' : makeNumber(locale)(value))

const dedupeByPeriod = <T extends { period?: string | null }>(rows: readonly T[]): T[] => {
  const byPeriod = new Map<string, T>()
  for (const row of rows) if (row.period) byPeriod.set(row.period, row)
  return [...byPeriod.values()]
}

const averageBy = <T>(rows: readonly T[], pick: (row: T) => number | null | undefined): number | null => {
  const values = rows.map(pick).filter((value): value is number => value != null)
  if (!values.length) return null
  return values.reduce((sum, value) => sum + value, 0) / values.length
}

const pickResponses = (results: BuyerResults) => {
  let clickout: ClickoutResponse | undefined
  let visiting: VisitingCompaniesResponse | undefined
  for (const item of results) {
    const response = item.response
    if (!response || item.status !== 200) continue
    if (response.__typename === 'ManageInsightsClickoutRateResponse') clickout = response
    else if (response.__typename === 'ManageInsightsVisitingCompaniesResponse') visiting = response
  }
  return { clickout, visiting }
}

const windowTotal = (
  rows: ClickoutRow[],
  pick: (row: ClickoutRow) => number | null | undefined,
  window: InsightsComparisonWindow,
  locale: string,
): BuyerWindowTotal => {
  const pickOrZero = (row: ClickoutRow) => pick(row) ?? 0
  const current = sumInWindow(rows, pickOrZero, window.start, window.end)
  const trend = percentTrend(previousWindowRatio(rows, pickOrZero, window, current), locale)
  return {
    value: formatCount(roundEstimate(current), locale),
    trend,
    caption: visibilityCaption(window, trend !== undefined, locale),
  }
}

export const mapBuyerInsights = (
  query: BuyerInsightsQuery | null | undefined,
  { locale, window }: { locale: string; window: InsightsComparisonWindow },
): BuyerInsightsView => {
  const results = query?.manage?.insights?.results ?? []
  const { clickout, visiting } = pickResponses(results)
  const byMetric = new Map(results.map((item) => [item.metric, item]))

  const clickoutRows = dedupeByPeriod(clickout?.rows ?? [])
  const windowClickoutRows = inWindow(clickoutRows, window.start, window.end)
  const visitingRows = dedupeByPeriod(visiting?.rows ?? [])

  return {
    profileViews: windowTotal(clickoutRows, (row) => row.pageviewsEstimated, window, locale),
    clickOuts: windowTotal(clickoutRows, (row) => row.clickouts, window, locale),
    visitingCompanies: formatCount(roundEstimate(averageBy(visitingRows, (row) => row.demaskedCompanies)), locale),
    states: {
      clickout: clickout?.truncated
        ? 'error'
        : countsState(windowState(byMetric.get(CLICKOUT_METRIC), windowClickoutRows.length, clickoutRows.length)),
      visiting: metricState(byMetric.get(VISITING_METRIC), visitingRows.length),
    },
  }
}
